import { useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, useInView } from 'framer-motion';
import {
  ArrowRight,
  CloudRain,
  Wrench,
  PaintBucket,
  Building2,
  ShieldAlert,
  CheckCircle,
  Home as HomeIcon,
  Users,
  Briefcase,
  Phone
} from 'lucide-react';

const fadeUpVariants = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0 }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.15
    }
  }
};

const divisions = [
  {
    id: 'roofing',
    overline: '01 // Roofing & Waterproofing',
    icon: CloudRain,
    title: 'Roofing & Waterproofing',
    description: 'Comprehensive roofing and waterproofing solutions for all building types.',
    features: [
      'Structural timber restoration',
      'Advanced waterproofing systems',
      'Full-building roofing solutions',
      'Leak detection and repair',
      'Roof maintenance programs'
    ],
    image:
      'https://customer-assets.emergentagent.com/job_mobile-first-build-48/artifacts/hbxs9ooq_roofing.jpg'
  },
  {
    id: 'plumbing',
    overline: '02 // Plumbing Services',
    icon: Wrench,
    title: 'Plumbing Services',
    description: 'Professional plumbing services with PIRB registration #2925/10.',
    features: [
      'Leak detection, geyser replacements',
      'Complete plumbing services',
      'Emergency burst pipe response',
      'Specialized drainage management',
      'Water system installations'
    ],
    image:
      'https://images.unsplash.com/photo-1676210134188-4c05dd172f89?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NjZ8MHwxfHNlYXJjaHwxfHxwbHVtYmluZyUyMHNlcnZpY2VzfGVufDB8fHx8MTc4MTM4NDE5OHww&ixlib=rb-4.1.0&q=85'
  },
  {
    id: 'maintenance',
    overline: '03 // General Maintenance',
    icon: PaintBucket,
    title: 'General Maintenance',
    description: 'Comprehensive property maintenance for all building types.',
    features: [
      'Painting, corrective maintenance',
      'Preventative property maintenance',
      'Building repairs and touch-ups',
      'Storm damage repairs',
      'Insurance-related maintenance'
    ],
    image:
      'https://customer-assets.emergentagent.com/job_mobile-first-build-48/artifacts/v05911vt_building%20maintenance.jpg'
  },
  {
    id: 'construction',
    overline: '04 // Building Construction',
    icon: Building2,
    title: 'Building Construction',
    description: 'Full-service construction from new builds to major renovations.',
    features: [
      'New builds and renovations',
      'Structural and cosmetic repairs',
      'Interior and exterior work',
      'Commercial and residential',
      'Project management'
    ],
    image:
      'https://customer-assets.emergentagent.com/job_mobile-first-build-48/artifacts/yckfgxj4_building%20construction.jpg'
  },
  {
    id: 'asbestos',
    overline: '05 // Type 3 Asbestos Removal',
    icon: ShieldAlert,
    title: 'Type 3 (High-Risk) Asbestos Removal',
    description: 'Certified specialist division for high-risk asbestos removal.',
    features: [
      'Type 3 certified specialists',
      'Safe asbestos removal',
      'Full compliance and documentation',
      'Environmental safety protocols',
      'Post-removal certification'
    ],
    image:
      'https://customer-assets.emergentagent.com/job_mobile-first-build-48/artifacts/p01l5sja_Asbestos-Removal-image.jpg'
  }
];

function DivisionBlock({ division, index }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-100px' });
  const isReversed = index % 2 === 1;

  return (
    <section
      ref={ref}
      id={division.id}
      data-testid={`division-${division.id}`}
      className={`py-24 md:py-32 ${
        index % 2 === 0 ? 'bg-background' : 'bg-surface border-y border-border'
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div
          className={`grid grid-cols-1 lg:grid-cols-2 gap-16 items-center ${
            isReversed ? 'lg:[&>*:first-child]:order-2' : ''
          }`}
        >
          <motion.div
            initial={{ opacity: 0, x: isReversed ? 50 : -50 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8 }}
            className="relative"
          >
            <div className="image-hover-container aspect-[4/5] overflow-hidden">
              <img
                src={division.image}
                alt={division.title}
                className="w-full h-full object-cover"
              />
            </div>
          </motion.div>

          <motion.div
            variants={staggerContainer}
            initial="hidden"
            animate={inView ? 'visible' : 'hidden'}
          >
            <motion.span variants={fadeUpVariants} className="overline">
              {division.overline}
            </motion.span>
            <motion.h2
              variants={fadeUpVariants}
              className="font-heading font-light text-4xl md:text-5xl text-text-main mt-4 tracking-tight"
            >
              {division.title}
            </motion.h2>
            <motion.p
              variants={fadeUpVariants}
              className="mt-6 text-text-muted font-body text-base md:text-lg leading-relaxed"
            >
              {division.description}
            </motion.p>
            <motion.ul variants={fadeUpVariants} className="mt-8 space-y-3">
              {division.features.map((feature) => (
                <li key={feature} className="flex items-center gap-3 text-text-muted text-sm">
                  <CheckCircle size={14} className="text-primary flex-shrink-0" />
                  {feature}
                </li>
              ))}
            </motion.ul>
            <motion.div variants={fadeUpVariants} className="mt-10">
              <Link
                to="/contact"
                data-testid={`division-cta-${division.id}`}
                className="inline-flex items-center gap-2 text-primary font-mono text-xs uppercase tracking-widest hover:gap-4 transition-all"
              >
                Request a Quote
                <ArrowRight size={14} />
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

export default function Services() {
  const coverlandRef = useRef(null);
  const coverlandInView = useInView(coverlandRef, { once: true, margin: '-100px' });

  const coverlandIdealFor = [
    'Homeowners',
    'Landlords',
    'Property Managers',
    'Body Corporates',
    'Estate Agents',
    'Insurance Claims'
  ];

  const coverlandFixes = [
    'Roof leaks and storm damage',
    'Damp and waterproofing failures',
    'Plumbing issues and leaks',
    'Ongoing building maintenance'
  ];

  return (
    <main data-testid="services-page">
      {/* Hero Section */}
      <section className="relative min-h-[70vh] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://customer-assets.emergentagent.com/job_mobile-first-build-48/artifacts/yckfgxj4_building%20construction.jpg"
            alt="Our Services"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-background/60 via-background/80 to-background" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 pt-32">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <span className="overline mb-6 block">Our Services</span>
            <h1 className="font-heading font-light text-5xl sm:text-6xl md:text-7xl text-text-main tracking-tight">
              Five <span className="text-primary">Divisions.</span>
            </h1>
            <h2 className="font-heading font-light text-3xl sm:text-4xl md:text-5xl text-text-muted mt-4 tracking-tight italic">
              One trusted partner.
            </h2>
            <p className="mt-8 max-w-2xl text-text-muted font-body text-base md:text-lg leading-relaxed">
              Comprehensive insurance repair and building construction services delivered across
              South Africa, with specialist divisions led by experienced supervisors.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Divisions */}
      {divisions.map((division, index) => (
        <DivisionBlock key={division.id} division={division} index={index} />
      ))}

      {/* Coverland Trade Services */}
      <section
        ref={coverlandRef}
        data-testid="coverland-section"
        className="py-24 md:py-32 lg:py-48 bg-background"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            variants={staggerContainer}
            initial="hidden"
            animate={coverlandInView ? 'visible' : 'hidden'}
          >
            <motion.span variants={fadeUpVariants} className="overline">
              06 // Specialist Division
            </motion.span>
            <motion.h2
              variants={fadeUpVariants}
              className="font-heading font-light text-4xl md:text-5xl lg:text-6xl text-text-main mt-4 tracking-tight"
            >
              Coverland<br />
              <span className="text-primary">Trade Services</span>
            </motion.h2>
            <motion.p
              variants={fadeUpVariants}
              className="mt-6 max-w-2xl text-text-muted font-body text-base md:text-lg leading-relaxed"
            >
              Our specialized division focusing on roof repairs, waterproofing, and property
              maintenance for homes, complexes, rental properties, and commercial buildings across
              Cape Town.
            </motion.p>
          </motion.div>

          <motion.div
            variants={staggerContainer}
            initial="hidden"
            animate={coverlandInView ? 'visible' : 'hidden'}
            className="mt-16 grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12"
          >
            <motion.div
              variants={fadeUpVariants}
              className="bg-surface border border-border p-8 md:p-12"
            >
              <Users className="text-primary mb-6" size={32} strokeWidth={1} />
              <h3 className="font-heading text-2xl md:text-3xl text-text-main mb-6 tracking-tight">
                Ideal For
              </h3>
              <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {coverlandIdealFor.map((item) => (
                  <li key={item} className="flex items-center gap-3 text-text-muted text-sm">
                    <CheckCircle size={14} className="text-primary flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
            </motion.div>

            <motion.div
              variants={fadeUpVariants}
              className="bg-surface border border-border p-8 md:p-12"
            >
              <Briefcase className="text-primary mb-6" size={32} strokeWidth={1} />
              <h3 className="font-heading text-2xl md:text-3xl text-text-main mb-6 tracking-tight">
                We Fix the Problems Property Owners Worry About Most
              </h3>
              <ul className="space-y-3">
                {coverlandFixes.map((item) => (
                  <li key={item} className="flex items-center gap-3 text-text-muted text-sm">
                    <CheckCircle size={14} className="text-primary flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
            </motion.div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mt-12 flex flex-col sm:flex-row items-center justify-between gap-6 bg-surface border border-border p-8 md:p-10"
          >
            <div>
              <span className="font-mono text-xs uppercase tracking-widest text-text-muted">
                Service Area
              </span>
              <p className="font-heading text-2xl text-text-main mt-2 tracking-tight">
                Cape Town and surrounding areas
              </p>
            </div>
            <Link
              to="/contact"
              data-testid="coverland-cta-btn"
              className="btn-primary border border-primary text-primary px-8 py-4 font-mono text-xs uppercase tracking-widest hover:text-background transition-colors inline-flex items-center justify-center gap-2 flex-shrink-0"
            >
              Get Fast Quote
              <ArrowRight size={16} />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 md:py-32 bg-surface border-t border-border">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
          >
            <span className="overline">Get Started</span>
            <h2 className="font-heading font-light text-4xl md:text-5xl text-text-main mt-4 tracking-tight">
              Need Expert<br />
              <span className="text-primary">Construction Services?</span>
            </h2>
            <p className="mt-6 text-text-muted font-body text-base md:text-lg leading-relaxed max-w-xl mx-auto">
              Our team is ready to help with your project. Contact us for a professional quote.
            </p>
            <div className="mt-12 flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/contact"
                data-testid="services-request-quote-btn"
                className="btn-primary border border-primary text-primary px-8 py-4 font-mono text-xs uppercase tracking-widest hover:text-background transition-colors inline-flex items-center justify-center gap-2"
              >
                Request a Quote
                <ArrowRight size={16} />
              </Link>
              <a
                href="tel:+27825697495"
                className="border border-text-muted/30 text-text-main px-8 py-4 font-mono text-xs uppercase tracking-widest hover:border-text-main transition-colors inline-flex items-center justify-center gap-2"
              >
                <Phone size={16} />
                082 569 7495
              </a>
            </div>
          </motion.div>
        </div>
      </section>
    </main>
  );
}
